import React from 'react';
import { FolderOpen, MapPin, ChevronRight, Layers } from 'lucide-react';
import { useProjectStore } from '../stores/useProjectStore';
import { useSupabaseProjects } from '../hooks/useSupabaseProjects';
import { useSupabaseScenes } from '../hooks/useSupabaseScenes';
import { useUiStore } from '../stores/useUiStore';
import type { Scene } from '../types';

/** Map a Supabase scene list item to the legacy Scene shape for display. */
function toScene(item: import('../hooks/useSupabaseScenes').SupabaseSceneListItem): Scene {
  return {
    id: item.id,
    name: item.name,
    fileName: item.name,
    fileType: 'las',
    pointCount: item.point_count,
    boundingBox: item.bounding_box ?? { min: { x: 0, y: 0, z: 0 }, max: { x: 0, y: 0, z: 0 } },
    createdAt: item.created_at,
  };
}

export const ProjectList: React.FC = () => {
  const { data: projects, isLoading, error } = useSupabaseProjects();
  const activeSceneId = useProjectStore((s) => s.activeSceneId);
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const setActiveScene = useProjectStore((s) => s.setActiveScene);
  const setActiveProject = useProjectStore((s) => s.setActiveProject);
  const showProjectPanel = useUiStore((s) => s.showProjectPanel);

  // Fetch scenes for the active project
  const { data: activeScenes } = useSupabaseScenes(activeProjectId);

  const allProjects = React.useMemo(() => {
    if (!projects) return [];
    return projects.map((project) => ({
      ...project,
      scenes:
        project.id === activeProjectId
          ? (activeScenes ?? []).map(toScene)
          : [],
    }));
  }, [projects, activeProjectId, activeScenes]);

  if (!showProjectPanel) {
    return null;
  }

  return (
    <div className="flex h-full flex-col bg-surface-elevated border-r border-slate-700/50 w-sidebar">
      <div className="flex items-center gap-2 border-b border-slate-700/50 px-4 py-3">
        <Layers className="h-5 w-5 text-sky-400" />
        <h1 className="text-base font-bold text-white">Cartografika</h1>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {isLoading && (
          <div className="px-3 py-6 text-center text-sm text-slate-500">
            Loading projects…
          </div>
        )}

        {error && (
          <div className="mx-2 rounded-lg bg-red-900/30 p-3 text-xs text-red-300 border border-red-800/50">
            Failed to load projects.
          </div>
        )}

        {allProjects.length === 0 && !isLoading && (
          <div className="px-3 py-6 text-center text-sm text-slate-500">
            No projects yet. Upload a file to get started.
          </div>
        )}

        {allProjects.map((project) => {
          const isActive = project.id === activeProjectId;
          return (
            <div key={project.id} className="mb-2">
              <button
                onClick={() => setActiveProject(isActive ? null : project.id)}
                className={[
                  'flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left transition-colors min-h-touch',
                  isActive
                    ? 'bg-sky-900/30 text-sky-300'
                    : 'text-slate-300 hover:bg-slate-800/70',
                ].join(' ')}
              >
                <FolderOpen className="h-4 w-4 shrink-0" />
                <span className="flex-1 truncate text-sm font-medium">
                  {project.name}
                </span>
                <ChevronRight
                  className={[
                    'h-4 w-4 shrink-0 text-slate-500 transition-transform',
                    isActive && 'rotate-90',
                  ].join(' ')}
                />
              </button>

              {isActive && (
                <div className="mt-1 ml-4 flex flex-col gap-0.5 border-l border-slate-700/50 pl-2">
                  {project.scenes.map((scene) => {
                    const sceneActive = scene.id === activeSceneId;
                    return (
                      <button
                        key={scene.id}
                        onClick={() => setActiveScene(scene.id)}
                        className={[
                          'flex items-center gap-2 rounded-md px-3 py-2 text-left transition-colors min-h-touch',
                          sceneActive
                            ? 'bg-sky-900/40 text-sky-300'
                            : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-300',
                        ].join(' ')}
                      >
                        <MapPin className="h-3.5 w-3.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-sm">{scene.name}</div>
                          <div className="text-xs text-slate-500">
                            {scene.pointCount.toLocaleString()} pts
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
